import React from "react";
import { BrowserRouter as Router } from "react-router-dom";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import { GlobalStyles } from "orcs-design-system";
import { ThemeProvider } from "styled-components";
import debug from "debug";

import { createRoot } from "react-dom/client";

import {
  StandardErrorBoundary,
  FailsafeErrorBoundary,
} from "./components/ErrorBoundary";

import { Auth0Provider } from "./contexts/auth0Provider";
import { getAuth0ConnectionFromHostname } from "./services/auth";
import { getAuth0ConfigForRegion } from "./config/auth0Tenants";
import { initErrorReporting } from "./services/errorReporting";
import { reloadOnPreloadError } from "./util/reloadOnPreloadError";
import App from "./app";
import setupFontAwesome, { GlobalCustomSVGStyles } from "./setupFontAwesome";
import setupMomentLocale from "./setupMomentLocale";
import useOnMount from "./hooks/useOnMount";
import useThemeSwitching from "./hooks/useThemeSwitching";
import { ThemeSwitchingProvider } from "./contexts/ThemeSwitchingContext";
import useFetchConfig from "./hooks/useFetchConfig";
import {
  GlobalSearchProvider,
  GlobalSearchDefaultScopeProvider,
} from "./components/Search/GlobalSearchContext";

import "./localization";

window.LOG_LEVEL = process.env.REACT_APP_LOG_LEVEL;
debug.enable(process.env.REACT_APP_DEBUG);
// following sentry's advice to do this as soon as possible:
initErrorReporting();
reloadOnPreloadError();

// Register SVG icons before first paint so Icon/FontAwesome lookups are never empty.
setupFontAwesome();

const AppShell = () => {
  const themeSwitching = useThemeSwitching();
  const { theme, themeSwitcher, isDarkTheme } = themeSwitching;
  const { config, isGenericDomain, isFetchingConfig } = useFetchConfig();

  useOnMount(() => {
    setupMomentLocale();
  });

  if (isFetchingConfig) {
    return null;
  }

  const bypassSSO =
    new URLSearchParams(window.location.search).get("bypass-sso") === "1";
  const connection = !bypassSSO
    ? getAuth0ConnectionFromHostname(config?.auth0Connection)
    : null;

  // Regional SPA settings come from auth0Tenants when KVS provides auth0TenantRegion.
  // REACT_APP_AUTH0_* is fallback only (e.g. hosts without a KVS entry, offline).
  const auth0Config = config?.auth0TenantRegion
    ? getAuth0ConfigForRegion(config.auth0TenantRegion)
    : null;

  const audience =
    auth0Config?.audience || process.env.REACT_APP_AUTH0_AUDIENCE;
  const clientId =
    auth0Config?.clientId || process.env.REACT_APP_AUTH0_CLIENT_ID;
  const domain = auth0Config?.domain || process.env.REACT_APP_AUTH0_DOMAIN;

  // material-react-table (via orcs Table) requires MUI theme context
  const muiTheme = createTheme({
    palette: { mode: isDarkTheme ? "dark" : "light" },
  });
  const breakpoints = [...theme.breakpoints];
  breakpoints.keys = muiTheme.breakpoints.keys;
  breakpoints.up = muiTheme.breakpoints.up;
  breakpoints.down = muiTheme.breakpoints.down;
  breakpoints.values = muiTheme.breakpoints.values;
  const themeWithMui = {
    ...theme,
    breakpoints,
    palette: { ...theme.palette, ...muiTheme.palette },
    shadows: { ...theme.shadows, ...muiTheme.shadows },
  };

  return (
    <MuiThemeProvider theme={muiTheme}>
      <ThemeProvider theme={themeWithMui}>
        <ThemeSwitchingProvider value={themeSwitching}>
          <GlobalSearchDefaultScopeProvider>
            <GlobalSearchProvider>
              <GlobalStyles />
              <GlobalCustomSVGStyles />
              <FailsafeErrorBoundary>
                <Router>
                  <Auth0Provider
                    audience={audience}
                    client_id={clientId}
                    domain={domain}
                    isGenericDomain={isGenericDomain}
                    redirect_uri={window.location.origin}
                    {...connection}
                  >
                    <StandardErrorBoundary>
                      <App themeSwitcher={themeSwitcher} />
                    </StandardErrorBoundary>
                  </Auth0Provider>
                </Router>
              </FailsafeErrorBoundary>
            </GlobalSearchProvider>
          </GlobalSearchDefaultScopeProvider>
        </ThemeSwitchingProvider>
      </ThemeProvider>
    </MuiThemeProvider>
  );
};

createRoot(document.getElementById("root")).render(<AppShell />);
